/**
 * Mix of feeds for one feeding (#361): "90% 7701 + 10% 7702". The feeding's amount stays the
 * total; the kg of each product is shown as total x percent. A feeding with no mix is the single
 * feed type it has always been.
 *
 * Two modes: a saved feeding saves its mix itself (onChange); an unsaved slot form passes `draft`
 * and the mix is written into the slot draft, to be sent with the one create request.
 */
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { FEED_TYPE_OPTIONS, feedTypeNames } from '@/lib/feedTypes';
import { MIX_INCOMPLETE, NEW_MIX_DRAFT, buildBlend, mixProblem, type FeedMixDraft } from '@/lib/feedMix';
import type { FeedBlendItem } from '@/types/entry';

const num = (v: number | string) => Number(v);
const kg = (v: number | string) => String(Math.round(num(v) * 1000) / 1000);
const pct = (v: number | string) => String(Math.round(num(v) * 100) / 100);

interface Props {
  /** The feeding's feed type: the main feed when a mix is started. */
  mainType: string | null;
  blend: FeedBlendItem[] | null | undefined;
  /** The feeding's total: each product shows total x percent. No amount yet: percents only. */
  totalKg: number | string | null;
  disabled?: boolean;
  /** A list saves that mix; null removes it. */
  onChange?: (blend: FeedBlendItem[] | null) => void;
  /** Unsaved slot: the mix lives in the slot draft instead of being saved here. */
  draft?: {
    value: FeedMixDraft | null;
    onChange: (value: FeedMixDraft | null) => void;
    /** The second feed was chosen or the percent was left: a held save may go ahead. */
    onSettled: (value: FeedMixDraft) => void;
  };
}

interface FieldsProps {
  main: string | null;
  totalKg: number | string | null;
  secondType: string;
  secondPercent: string;
  error: string | null;
  onSecondType: (v: string) => void;
  onSecondPercent: (v: string) => void;
  onPercentBlur?: () => void;
  selectRef?: React.Ref<HTMLSelectElement>;
}

function MixFields({ main, totalKg, secondType, secondPercent, error, onSecondType, onSecondPercent, onPercentBlur, selectRef }: FieldsProps) {
  const second = Number(secondPercent);
  const first = Math.round((100 - second) * 100) / 100;
  const hasTotal = totalKg !== null && totalKg !== '' && Number.isFinite(num(totalKg));
  return (
    <>
      <p className="text-sm font-medium text-gray-800">
        {Number.isFinite(second) && !error ? `${pct(first)}% ${main}` : `${main}`} mixed with
      </p>
      {!error && (
        <p className="mt-0.5 text-xs text-gray-500" data-feed-mix-preview>
          {hasTotal
            ? `${main}: ${kg((num(totalKg) * first) / 100)} kg · second feed: ${kg((num(totalKg) * second) / 100)} kg`
            : `${main}: ${pct(first)}% · second feed: ${pct(second)}%`}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <select
          ref={selectRef}
          value={secondType}
          onChange={(e) => onSecondType(e.target.value)}
          aria-label="Second feed"
          className="min-h-[44px] w-full min-w-0 rounded-lg border border-gray-300 bg-white px-3 text-sm sm:w-auto sm:flex-1"
        >
          <option value="">Choose a feed</option>
          {FEED_TYPE_OPTIONS.filter((o) => o.value !== main).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-700">
          <input
            inputMode="decimal"
            value={secondPercent}
            onChange={(e) => onSecondPercent(e.target.value)}
            onBlur={onPercentBlur}
            aria-label="Second feed percent"
            className="min-h-[44px] w-20 rounded-lg border border-gray-300 px-3 text-right tabular-nums"
          />
          %
        </label>
      </div>
      {error && (
        // Not yet filled in is a prompt, not an error to announce on open.
        <p role={MIX_INCOMPLETE.includes(error) ? 'status' : 'alert'} className="mt-1.5 text-xs text-red-600">
          {error}
        </p>
      )}
    </>
  );
}

export function FeedMix({ mainType, blend, totalKg, disabled, onChange, draft }: Props) {
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [secondType, setSecondType] = useState('');
  const [secondPercent, setSecondPercent] = useState('');
  const main = blend?.[0]?.feed_type ?? mainType;
  // Keyboard focus follows the draft editor opening and closing.
  const selectRef = useRef<HTMLSelectElement>(null);
  const linkRef = useRef<HTMLButtonElement>(null);
  const focusNext = useRef<'select' | 'link' | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  // Focus never leaves a text box outside this editor (a typed amount keeps its box; leaving it would save it).
  const wantFocus = (to: 'select' | 'link') => {
    const active = document.activeElement;
    const typing = !!active && ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName) && !editorRef.current?.contains(active);
    focusNext.current = typing ? null : to;
  };
  const draftOpen = !!draft?.value;
  useEffect(() => {
    if (focusNext.current === 'select') selectRef.current?.focus();
    else if (focusNext.current === 'link') linkRef.current?.focus();
    focusNext.current = null;
  }, [draftOpen]);

  const startEdit = () => {
    setSecondType(blend?.[1]?.feed_type ?? '');
    setSecondPercent(blend?.[1] ? pct(blend[1].percent) : '');
    setEditing(true);
  };

  if (draft) {
    if (!draft.value) {
      if (disabled || !main) return null;
      return (
        <button
          type="button"
          ref={linkRef}
          // Keeps a typed amount in its box: pressing the link must not blur it into a plain save.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            wantFocus('select');
            draft.onChange(NEW_MIX_DRAFT);
          }}
          className="mt-1 min-h-[44px] text-sm font-semibold text-primary-600"
          data-feed-mix-add
        >
          + Add a second feed
        </button>
      );
    }
    const value = draft.value;
    return (
      <div ref={editorRef} className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3" data-feed-mix-editor data-feed-mix-draft>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <MixFields
              main={main}
              totalKg={totalKg}
              secondType={value.feed_type}
              secondPercent={value.percent}
              error={mixProblem(main, value.feed_type, value.percent)}
              onSecondType={(feed_type) => {
                const next = { ...value, feed_type };
                draft.onChange(next);
                draft.onSettled(next);
              }}
              onSecondPercent={(percent) => draft.onChange({ ...value, percent })}
              onPercentBlur={() => draft.onSettled(value)}
              selectRef={selectRef}
            />
          </div>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              wantFocus('link');
              draft.onChange(null);
            }}
            aria-label="Remove second feed"
            title="Remove second feed"
            className="-mr-1 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-lg text-gray-500 hover:bg-gray-100"
          >
            ✕
          </button>
        </div>
      </div>
    );
  }

  const error = mixProblem(main, secondType, secondPercent);
  const second = Number(secondPercent);

  if (editing) {
    return (
      <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3" data-feed-mix-editor>
        <MixFields
          main={main}
          totalKg={totalKg}
          secondType={secondType}
          secondPercent={secondPercent}
          error={error}
          onSecondType={setSecondType}
          onSecondPercent={setSecondPercent}
        />
        <div className="mt-2 flex justify-end gap-2">
          <button type="button" onClick={() => setEditing(false)} className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700">
            Cancel
          </button>
          <button
            type="button"
            disabled={!!error || disabled}
            onClick={() => {
              onChange?.(buildBlend(main as string, secondType, second));
              setEditing(false);
            }}
            className="min-h-[44px] rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white disabled:opacity-50"
          >
            Save mix
          </button>
        </div>
      </div>
    );
  }

  if (blend && blend.length > 0) {
    return (
      <div className="mt-1.5" data-feed-mix>
        <ul className="space-y-0.5 text-sm text-gray-800">
          {blend.map((b) => (
            <li key={b.feed_type} className="flex items-baseline justify-between gap-3">
              <span>
                {pct(b.percent)}% {b.feed_type}
                {feedTypeNames(b.feed_type) ? <span className="text-gray-500"> ({feedTypeNames(b.feed_type)})</span> : null}
              </span>
              <span className="tabular-nums text-gray-600">{kg((num(totalKg ?? 0) * num(b.percent)) / 100)} kg</span>
            </li>
          ))}
        </ul>
        {!disabled && (
          <div className="mt-1 flex gap-3">
            {blend.length === 2 ? (
              <button type="button" onClick={startEdit} className="min-h-[44px] text-sm font-semibold text-primary-600">
                Edit mix
              </button>
            ) : (
              <span className="py-2 text-xs text-gray-500">This mix has {blend.length} feeds: it can only be removed here.</span>
            )}
            {confirmRemove ? (
              <span className="flex items-center gap-2 text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setConfirmRemove(false);
                    onChange?.(null);
                  }}
                  className="min-h-[44px] font-semibold text-red-700"
                >
                  Yes, remove
                </button>
                <button type="button" onClick={() => setConfirmRemove(false)} className="min-h-[44px] font-semibold text-gray-600">
                  Keep
                </button>
              </span>
            ) : (
              <button type="button" onClick={() => setConfirmRemove(true)} className="min-h-[44px] text-sm font-semibold text-gray-600">
                Remove mix
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (disabled || !main) return null;
  return (
    <button type="button" onClick={startEdit} className={cn('mt-1 min-h-[44px] text-sm font-semibold text-primary-600')} data-feed-mix-start>
      Mix with another feed
    </button>
  );
}
